// Home Screen / taskbar badge = cards waiting in today's session (due reviews + today's new words
// + follow-ups), the same list Tänään builds. Set while the app is open: at start, after every
// change of cards or shared state (each grade, each sync pull), when the app comes back to the
// foreground, and every 15 minutes (the day can roll over). 0 clears it. Page side only; the
// helper is lib/badge.ts.
import { useEffect } from 'react';
import { badgeSupported, setAppBadgeCount } from '../lib/badge';
import { store, useApp } from '../state/store';

// The count is built by the session builder (lib/session.ts → lib/forms.ts → the paradigm
// generator), which the start-up chunk does not carry: load it when the badge is first set.
let counter: Promise<typeof import('./badgeCount')> | null = null;
function setBadgeFromStore(): void {
  // Today's session on screen keeps its own count: no rebuild after every grade.
  const left = store.get().sessionLeft;
  if (left !== null) {
    void setAppBadgeCount(left).catch(() => undefined);
    return;
  }
  counter ??= import('./badgeCount');
  void counter
    .then((m) => (store.get().phase === 'ready' ? setAppBadgeCount(m.cardsWaitingToday(store.get())) : undefined))
    .catch(() => {
      // Never let the badge break the shell.
    });
}

const REFRESH = 15 * 60 * 1000;

export function useAppBadge(): void {
  const phase = useApp((s) => s.phase);
  const cards = useApp((s) => s.cards);
  const shared = useApp((s) => s.shared);
  const lastSync = useApp((s) => s.sync.lastSync);
  const course = useApp((s) => s.course);
  const lessonProgress = useApp((s) => s.lessonProgress);
  const inSession = useApp((s) => s.inSession);
  const sessionLeft = useApp((s) => s.sessionLeft);

  useEffect(() => {
    if (badgeSupported() && sessionLeft !== null) setBadgeFromStore();
  }, [sessionLeft]);

  useEffect(() => {
    // During a session (daily or free) each grade changes the cards: the full count waits until
    // the session ends (inSession → false); the daily session's own count covers the meantime.
    if (!badgeSupported() || phase !== 'ready' || inSession) return;
    // Debounced: a sync pull or a burst of grades changes the cards several times in a row.
    const id = window.setTimeout(setBadgeFromStore, 800);
    return () => window.clearTimeout(id);
  }, [phase, cards, shared, lastSync, course, lessonProgress, inSession]);

  useEffect(() => {
    if (!badgeSupported()) return;
    const update = () => {
      // Also on 'hidden': the last grade's debounce may not have fired before the app was left.
      if (store.get().phase === 'ready') setBadgeFromStore();
    };
    document.addEventListener('visibilitychange', update);
    const timer = window.setInterval(update, REFRESH);
    return () => {
      document.removeEventListener('visibilitychange', update);
      window.clearInterval(timer);
    };
  }, []);
}
